import { useState, useRef, useEffect } from "react";
import FieldToast from "../components/FieldToast";
import "./Careers.css";
import { DEPARTMENTS, POSITIONS_BY_DEPARTMENT } from "./departments";
import { createApplication, getAllAnnouncements } from "../api/api";
import { extractCollection } from "../utils/extractCollection";

/* ───────────────── MODAL ───────────────── */

function ApplicationModal({ job, onClose }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    portfolioUrl: "",
    department: job?.department || "",
    position: job?.position || job?.title || "",
    message: "",
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef(null);

  // Field refs for validation toasts
  const fullNameRef = useRef(null);
  const emailRef = useRef(null);
  const resumeRef = useRef(null);

  // Field toast state
  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({
      show: true,
      message,
      targetRef,
    });
    setTimeout(() => {
      setFieldToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const availablePositions =
    POSITIONS_BY_DEPARTMENT[form.department] || [];

  const validate = () => {
    const e = {};

    if (typeof form.fullName !== 'string' || !form.fullName.trim()) {
      showFieldToast("Full name is required", fullNameRef);
      return false;
    }

    if (typeof form.email !== 'string' || !form.email.trim()) {
      showFieldToast("Email is required", emailRef);
      return false;
    }

    if (!resumeFile) {
      showFieldToast("Resume is required", resumeRef);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      await createApplication({
        ...form,
        title: job?.title || form.position,
        jobTitle: job?.title || form.position,
        jobId: job?._id || job?.id,
        announcementId: job?._id || job?.id,
        resumeFile,
      });

      setSuccess(true);
    } catch (err) {
      setErrors({
        api: err.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors({
        resume: "Only PDF or DOC/DOCX allowed",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({
        resume: "File too large (max 5MB)",
      });
      return;
    }

    setResumeFile(file);

    setErrors((prev) => ({
      ...prev,
      resume: "",
    }));
  };

  const setField = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  useEffect(() => {
    const esc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", esc);

    return () => {
      window.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal">

        <button className="modal-close" onClick={onClose}>
          ×
        </button>

        {success ? (
          <div className="success-state">
            <h2>Application Sent Successfully ✅</h2>
          </div>
        ) : (
          <>
            <h2 className="modal-title">
              {job?.title || "Open Application"}
            </h2>

            {errors.api && (
              <div className="api-error">
                {errors.api}
              </div>
            )}

            <div className="form-group">
              <input
                ref={fullNameRef}
                type="text"
                placeholder="Full Name"
                value={form.fullName}
                onChange={setField("fullName")}
              />
            </div>

            <div className="form-group">
              <input
                ref={emailRef}
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={setField("email")}
              />
            </div>

            <div className="form-group">
              <input
                type="tel"
                placeholder="Phone"
                value={form.phone}
                onChange={setField("phone")}
              />
            </div>

            <div className="form-group">
              <select
                value={form.department}
                onChange={setField("department")}
              >
                <option value="">Department</option>

                {DEPARTMENTS.map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
              </select>
            </div>

            {availablePositions.length > 0 && (
              <div className="form-group">
                <select
                  value={form.position}
                  onChange={setField("position")}
                >
                  <option value="">Position</option>

                  {availablePositions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <textarea
                placeholder="Why do you want to join us?"
                value={form.message}
                onChange={setField("message")}
              />
            </div>

            <div
              ref={resumeRef}
              className="dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  handleFile(e.target.files[0])
                }
              />

              {resumeFile ? (
                <span>{resumeFile.name}</span>
              ) : (
                <span>Upload Resume</span>
              )}
            </div>

            {errors.resume && (
              <span className="error-msg">
                {errors.resume}
              </span>
            )}

            <button
              className="btn-submit"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </>
        )}

        <FieldToast
          message={fieldToast.message}
          targetRef={fieldToast.targetRef}
          show={fieldToast.show}
        />
      </div>
    </div>
  );
}

/* ───────────────── PAGE ───────────────── */

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedJob, setSelectedJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const data = await getAllAnnouncements();
        const list = extractCollection(data).filter((job) => job.publish !== false && job.published !== false);
        setJobs(list);
      } catch (err) {
        console.error(err);
        setError("Failed to load jobs. Please try again later.");
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const openModal = (job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  return (
    <div className="careers-page">

      <section className="hero">
        <h1>We're Hiring</h1>
      </section>

      <section className="jobs-grid">

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p style={{ textAlign: 'center', color: '#e05252', padding: '40px' }}>
            {error}
          </p>
        ) : jobs.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b6660', padding: '40px' }}>
            No open positions at the moment. Check back later!
          </p>
        ) : (
          jobs.map((job) => (
            <div
              className="job-card"
              key={job._id}
              onClick={() => openModal(job)}
            >
              <h3>{job.title}</h3>

              <p>{job.description}</p>
            </div>
          ))
        )}

      </section>

      {modalOpen && (
        <ApplicationModal
          job={selectedJob}
          onClose={() => setModalOpen(false)}
        />
      )}

    </div>
  );
}